// FE-SCRM-011 — BOR workflow panel.
//
// Renders inside the Opportunity detail page when
// ``opportunity_type === "bor_takeover"``. The producer sees:
//
// * Incumbent broker + target effective date as context (read-only
//   here; full edit lives in the Opportunity form once that surface
//   exists).
// * A prominent "Generate BOR letter" button.
// * On 422: a labeled checklist of missing fields. We map the BE's
//   field codes to broker-readable labels here so the user sees
//   "Prospect address" rather than ``prospect_address``.
// * On success: a signed download link.
//
// Mutation state is owned by ``useGenerateBorLetterMutation`` —
// success-side cache write-through lives there, not in this
// component, so other parts of the page (e.g. the activity timeline)
// pick up the new activity without prop drilling.

import { useEffect, useRef, useState } from "react";

import { ApiError } from "@/api/client";
import { Toast } from "@/design-system";

import { useGenerateBorLetterMutation } from "./hooks";
import type {
  BorRequiredField,
  GenerateBorLetterResponse,
  OpportunityDetail,
} from "./types";


// FE-SCRM-038 — BOR-letter celebratory micro-interaction.
// Producers who generate a BOR letter just earned the most
// consequential moment in the sales cycle (the takeover is
// committed). A short-lived pulse + Toast acknowledges the win
// without blocking the next step (which is to send the letter to
// the carrier). The class name is part of the contract a vitest
// pin asserts against.
const CELEBRATION_CLASS = "bor-celebration";
const CELEBRATION_DURATION_MS = 2_400;


const FIELD_LABELS: Record<BorRequiredField, string> = {
  prospect_address: "Prospect address",
  bor_effective_date: "BOR effective date",
  incumbent_broker_firm: "Incumbent broker",
  producer: "Producer (signing party)",
};


function labelFor(code: string): string {
  if (code in FIELD_LABELS) {
    return FIELD_LABELS[code as BorRequiredField];
  }
  // Fall back to a humanized form for any new code the BE introduces
  // before the FE catches up — preserves "no raw enum bleed" without
  // pinning the FE to ship simultaneously with every BE addition.
  return code.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}


export interface BorPanelProps {
  opportunity: OpportunityDetail;
}


export function BorPanel({ opportunity }: BorPanelProps) {
  const mutation = useGenerateBorLetterMutation(opportunity.opportunity_id);
  const missing = extractMissing(mutation.error);
  const result: GenerateBorLetterResponse | undefined = mutation.data;
  const otherError = !missing && mutation.isError;

  // FE-SCRM-038 — celebration state. Flipped on transitions of
  // mutation.isSuccess; auto-dismisses after CELEBRATION_DURATION_MS
  // so the pulse fades on its own without producer intervention.
  // The Toast separately stays mounted so producers see the next-
  // step copy ("Time to send it.") regardless of the pulse window.
  const [celebrating, setCelebrating] = useState(false);
  const [celebrationToastOpen, setCelebrationToastOpen] = useState(false);
  const lastResultId = useRef<string | null>(null);

  useEffect(() => {
    if (!result || !mutation.isSuccess) return;
    // Re-running the mutation produces a new bor_letter_id; guard
    // against re-firing the celebration on re-renders that don't
    // correspond to a fresh success.
    // Re-generation produces a new activity_id even if the prior
    // call cached a download_url; key the dedupe on activity_id so
    // a second generation re-fires the celebration.
    const resultId = result.activity_id ?? result.link_id ?? null;
    if (resultId === lastResultId.current) return;
    lastResultId.current = resultId;
    setCelebrating(true);
    setCelebrationToastOpen(true);
    const t = window.setTimeout(
      () => setCelebrating(false), CELEBRATION_DURATION_MS,
    );
    return () => window.clearTimeout(t);
  }, [mutation.isSuccess, result]);

  return (
    <section
      data-testid="bor-panel"
      className={[
        "relative rounded-md border border-border-subtle bg-raised p-4",
        // Celebration pulse — short-lived ring + scale so the
        // generate button visibly "blooms" once. Tailwind's
        // ``animate-pulse`` would loop indefinitely; we want a
        // single-shot accent that lasts CELEBRATION_DURATION_MS.
        celebrating
          ? `${CELEBRATION_CLASS} ring-2 ring-teal/60 shadow-lg shadow-teal/20 transition-shadow duration-500`
          : "",
      ].join(" ")}
      data-celebrating={celebrating ? "true" : undefined}
    >
      <header className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-text-primary">
          BOR letter
        </h2>
        <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
          BOR takeover
        </span>
      </header>

      <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div>
          <dt className="text-text-tertiary">Incumbent broker</dt>
          <dd
            className="text-text-primary"
            data-testid="bor-incumbent-broker"
          >
            {opportunity.incumbent_broker || "—"}
          </dd>
        </div>
        <div>
          <dt className="text-text-tertiary">Target effective date</dt>
          <dd
            className="text-text-primary"
            data-testid="bor-effective-date"
          >
            {opportunity.bor_effective_date ?? "—"}
          </dd>
        </div>
      </dl>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          data-testid="bor-generate-button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          className={[
            "rounded-md border border-border-subtle bg-recessed px-3 py-1.5",
            "text-xs font-medium text-text-primary",
            "hover:bg-raised-hover disabled:cursor-not-allowed",
            "disabled:opacity-60",
          ].join(" ")}
        >
          {mutation.isPending ? "Generating…" : "Generate BOR letter"}
        </button>
        {result && result.download_url ? (
          <a
            href={result.download_url}
            data-testid="bor-download-link"
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-teal underline"
          >
            Download {result.filename}
          </a>
        ) : null}
      </div>

      {/* Celebration sparkle — single emoji + visually-hidden text
          for screen readers. The element only renders during the
          pulse window, so vitest can pin both presence + the
          ``bor-celebration`` class hook. */}
      {celebrating ? (
        <span
          data-testid="bor-celebration-sparkle"
          aria-hidden="true"
          className={[
            CELEBRATION_CLASS,
            "pointer-events-none absolute right-3 top-3",
            "text-base motion-safe:animate-bounce",
          ].join(" ")}
        >
          🎉
        </span>
      ) : null}

      {missing ? <MissingFieldsGate fields={missing} /> : null}

      {otherError ? (
        <p
          data-testid="bor-error"
          className="mt-3 text-xs text-text-danger"
        >
          Could not generate the BOR letter. Try again, or contact
          support if the problem persists.
        </p>
      ) : null}

      {celebrationToastOpen ? (
        <Toast
          variant="success"
          title="BOR letter generated 🎉 Time to send it."
          description="Forward the signed letter to the incumbent and the new carrier to lock in the takeover."
          onDismiss={() => setCelebrationToastOpen(false)}
        />
      ) : null}
    </section>
  );
}


function MissingFieldsGate({ fields }: { fields: string[] }) {
  return (
    <div
      data-testid="bor-missing-fields"
      role="alert"
      className={[
        "mt-3 rounded-md border border-amber-300/40 bg-amber-50/40 p-3",
        "text-xs text-text-primary",
      ].join(" ")}
    >
      <p className="font-medium">
        Add these before generating the BOR letter:
      </p>
      <ul className="mt-1 list-disc pl-4">
        {fields.map((code) => (
          <li key={code} data-testid={`bor-missing-${code}`}>
            {labelFor(code)}
          </li>
        ))}
      </ul>
    </div>
  );
}


// Distinguish "API said the user has fields to fill in" from "the
// request errored for some other reason". The BE returns 422 with a
// ``missing`` array when fields are unpopulated; everything else
// (auth, network, 500) falls through to the generic error path.
function extractMissing(err: unknown): string[] | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status !== 422) return null;
    const m = (err.body as { missing?: unknown }).missing;
    if (Array.isArray(m) && m.every((v) => typeof v === "string")) {
      return m as string[];
    }
    return null;
  }
  // Tests reject with plain objects shaped like ``{ status, body }``;
  // mirror the runtime ApiError check so the mutation hook stays
  // testable without needing to construct a real ApiError instance.
  if (typeof err === "object" && err !== null) {
    const e = err as { status?: unknown; body?: { missing?: unknown } };
    if (e.status === 422 && e.body && Array.isArray(e.body.missing)) {
      const m = e.body.missing;
      if (m.every((v) => typeof v === "string")) return m as string[];
    }
  }
  return null;
}
